import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { categories, mainSiteUrl } from '@/mocks/categories';

const siteUrl = import.meta.env.VITE_SITE_URL || 'https://mulebuyspreadsheets.boutique';

const shoppingJsonLd = {
  '@context': 'https://schema.org',
  '@type': 'WebPage',
  '@id': `${siteUrl}/shopping-guide#webpage`,
  name: 'Complete Mulebuy Spreadsheet Shopping Directory',
  description: 'Browse all 14 Mulebuy Spreadsheet-powered product categories — Shoes, Hoodies, T-Shirts, Jackets, Pants, Headwear, Sets, Underwear, Jersey, Accessories, Bags, Belts, Perfumes, and OtherStuff — with direct links to the main platform.',
  url: `${siteUrl}/shopping-guide`,
  inLanguage: 'en',
  isPartOf: {
    '@type': 'WebSite',
    '@id': `${siteUrl}/#website`,
    name: 'Mulebuy Spreadsheet Hub',
    url: siteUrl,
  },
};

export default function ShoppingGuidePage() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-background-50">
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(shoppingJsonLd) }}
      />
      <Navbar />
      <main>
        <section className="pt-24 section-padding">
          <div className="container-custom">
            <div className="text-center mb-12">
              <span className="inline-flex items-center px-3 py-1.5 rounded-full bg-accent-100 text-accent-700 text-xs font-semibold font-label mb-4">
                Shopping Guide
              </span>
              <h1 className="text-3xl md:text-4xl lg:text-5xl font-heading font-bold text-foreground-900">
                Complete Mulebuy Spreadsheet <span className="italic text-accent-500">Shopping Directory</span>
              </h1>
              <p className="text-sm text-foreground-500 mt-4 max-w-2xl mx-auto">
                Browse all {categories.length} Mulebuy Spreadsheet-powered product categories. Each link takes you directly to the curated collection on the main platform.
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 md:gap-6">
              {categories.map((cat) => (
                <a
                  key={cat.seq}
                  href={cat.url}
                  target="_blank"
                  rel="noopener nofollow"
                  className="group flex items-start gap-4 p-4 rounded-xl bg-background-100 hover:bg-background-200/80 transition-all duration-300"
                >
                  <div className="w-16 h-16 flex-shrink-0 rounded-xl overflow-hidden">
                    <img
                      src={cat.imageUrl}
                      alt={`${cat.name} — Mulebuy Spreadsheet Shopping Guide`}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="text-sm font-semibold text-foreground-800 font-label group-hover:text-accent-600 transition-colors">
                      {cat.name}
                    </h3>
                    <p className="text-xs text-foreground-500 mt-1 leading-relaxed line-clamp-2">
                      {cat.description}
                    </p>
                    <span className="inline-flex items-center gap-1 mt-2 text-xs text-accent-600 font-label">
                      Shop Now <i className="ri-arrow-right-up-line text-[10px]"></i>
                    </span>
                  </div>
                </a>
              ))}
            </div>

            <div className="mt-12 p-6 rounded-xl bg-primary-900 text-background-50 text-center">
              <h3 className="text-lg font-heading font-bold mb-2">Ready for the full experience?</h3>
              <p className="text-sm text-background-300 mb-4">Visit the main platform to access all products, tools, and features.</p>
              <a
                href={mainSiteUrl}
                target="_blank"
                rel="noopener nofollow"
                className="inline-flex items-center gap-2 px-6 py-3 bg-accent-500 text-background-50 text-sm font-semibold rounded-md hover:bg-accent-600 transition-colors font-label"
              >
                Visit Main Platform <i className="ri-arrow-right-up-line"></i>
              </a>
            </div>

            <div className="mt-8 text-center">
              <Link to="/" className="inline-flex items-center gap-1.5 text-sm text-foreground-500 hover:text-accent-500 transition-colors font-label">
                <i className="ri-arrow-left-line"></i> Back to Home
              </Link>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}